import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  doc, getDoc, collection, query, where, getDocs
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faFilePdf, 
  faFileAlt, faSpinner,
  faCalendarAlt, faMapMarkerAlt,
  faQrcode, faPrint, faUser
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import FileViewerModal from './FileViewerModal';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import QRCode from "react-qr-code";
import styles from './EquipmentDetails.module.css';

const AssignedEquipmentCardDetails = () => {
  const location = useLocation();
  const { tourId, dateId, groupId } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);

  // State for data structure
  const [groupData, setGroupData] = useState(null);
  const [individualItems, setIndividualItems] = useState([]);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or itemId

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]);

  // QR code modal state
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedQrItem, setSelectedQrItem] = useState(null);

  // File viewer state
  const [fileViewerUrl, setFileViewerUrl] = useState('');
  const [fileViewerType, setFileViewerType] = useState('');
  const [fileViewerName, setFileViewerName] = useState('');

  // State for storing complete staff details
  const [staffDetails, setStaffDetails] = useState({});

  // App URL for QR codes
  const APP_URL = 'https://ontour.co.in';

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch the item details
  useEffect(() => {
    const fetchItemDetails = async () => {
      if (!tourId || !dateId || !groupId) {
        setIsLoading(false);
        setError('Missing required parameters');
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const db = getFirestoreInstance();
        
        // Get from the tour's assignedInventoryGroups collection
        const groupRef = doc(db, 'Tours', tourId, 'tourDates', dateId, 'assignedInventoryGroups', groupId);
        const groupSnap = await getDoc(groupRef);
        
        if (groupSnap.exists()) {
          const groupData = {
            groupId: groupSnap.id,
            ...groupSnap.data()
          };
          
          setGroupData(groupData);
          
          // Also fetch individual items in this group
          await fetchIndividualItems(groupData.groupId);
        } else {
          setError('Equipment item not found');
        }
      } catch (error) {
        console.error('Error fetching equipment details:', error);
        setError('Failed to load equipment details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchItemDetails();
  }, [tourId, dateId, groupId]);

  // Fetch individual items for the group
  const fetchIndividualItems = async (groupId) => {
    if (!groupId || !tourId || !dateId) return;
    
    setIsLoadingItems(true);
    
    try {
      const db = getFirestoreInstance();
      
      // Query the assignedInventoryItems subcollection
      const itemsQuery = query(
        collection(db, 'Tours', tourId, 'tourDates', dateId, 'assignedInventoryItems'),
        where('groupId', '==', groupId)
      );
      
      const itemsSnap = await getDocs(itemsQuery);
      
      if (!itemsSnap.empty) {
        const items = itemsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setIndividualItems(items);
        
        // If this is the first load, set the active tab to details
        if (items.length > 0 && activeTab === 'details') {
          setActiveTab('details');
        }
        
        // Fetch staff details if any items have staffIds
        const staffIds = new Set();
        items.forEach(item => {
          if (item.assignedStaff && Array.isArray(item.assignedStaff)) {
            item.assignedStaff.forEach(id => staffIds.add(id));
          }
        });
        
        if (staffIds.size > 0) {
          fetchStaffDetails(Array.from(staffIds));
        }
      } else {
        setIndividualItems([]);
      }
    } catch (error) {
      console.error('Error fetching individual items:', error);
    } finally {
      setIsLoadingItems(false);
    }
  };

  // Fetch staff details
  const fetchStaffDetails = async (staffIds) => {
    if (!staffIds.length) return;
    
    try {
      const db = getFirestoreInstance();
      const newDetailsMap = { ...staffDetails };
      
      await Promise.all(staffIds.map(async (staffId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', staffId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            newDetailsMap[staffId] = {
              id: staffId,
              fullName: userData.fullName || 'Unknown Staff',
              userName: userData.userName || '',
              profilePictureUrl: userData.profilePictureUrl || '',
              userSubType: userData.userSubType || '',
              email: userData.email || ''
            };
          }
        } catch (err) {
          console.error(`Error fetching staff details for ${staffId}:`, err);
        }
      }));
      
      setStaffDetails(newDetailsMap);
    } catch (error) {
      console.error('Error fetching staff details:', error);
    }
  };

  // Handle back button click
  const handleBack = () => {
    // If we have a fromEvent flag, go back to event with assigned inventory tab
    if (location.state?.fromEvent) {
      navigate(`/tour/${tourId}/${dateId}`, {
        state: { 
          activeTab: 'assignedInventory',
          vendorId: location.state?.vendorId 
        }
      });
    } else {
      // Regular back
      navigate(-1);
    }
  };

  // Handle QR code button click
  const handleQrCodeClick = (item) => {
    setSelectedQrItem(item);
    setShowQrModal(true);
  };

  // Print QR code
  const handlePrintQrCode = () => {
    const printContent = document.getElementById('qr-code-print-area');
    if (printContent) {
      // Create an iframe to print from (invisible)
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      // Write content to iframe
      iframe.contentDocument.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                text-align: center;
                padding: 20px;
              }
              .qr-container {
                display: inline-block;
                margin: 0 auto;
                padding: 15px;
                border: 1px solid #ddd;
                border-radius: 8px;
              }
              h3 {
                margin-top: 5px;
                margin-bottom: 3px;
                font-size: 32px;
              }
              p {
                margin: 3px 0;
                font-size: 32px;
                color: #E2384D;
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      
      iframe.contentDocument.close();
      
      // Focus and print
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }
  };

  // Handle view file
  const handleViewFile = (item) => {
    if (item.fileUrl) {
      setFileViewerUrl(item.fileUrl);
      setFileViewerName(item.name || getFileName(item.fileUrl));
      
      // Detect file type from URL extension
      const fileUrl = item.fileUrl.toLowerCase();
      if (fileUrl.endsWith('.pdf')) {
        setFileViewerType('application/pdf');
      } else if (fileUrl.match(/\.(jpe?g|png|gif|bmp|webp)$/)) {
        // Use the specific image type rather than generic 'image'
        const extension = fileUrl.split('.').pop();
        setFileViewerType(`image/${extension === 'jpg' ? 'jpeg' : extension}`);
      } else if (fileUrl.match(/\.(docx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      } else if (fileUrl.match(/\.(xlsx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } else {
        setFileViewerType('application/octet-stream');
      }
      
      setShowFileViewer(true);
    }
  };

  // Extract filename from fileUrl if present
  const getFileName = (url) => {
    if (!url) return 'Document';
    
    try {
      const parts = url.split('/');
      // Get the last part of the URL (filename)
      const rawFilename = parts[parts.length - 1];
      // Decode any URL encoded characters
      return decodeURIComponent(rawFilename);
    } catch (error) {
      console.error('Error extracting filename:', error);
      return 'Document';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get the formatted display name for the category
  const getCategoryDisplayName = (inventoryType) => {
    return inventoryType || 'Equipment';
  };

  // Tab rendering function
  const renderTabs = () => {
    return (
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tabButton} ${activeTab === 'details' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Overview
          </button>
          
          {individualItems.map(item => (
            <button
              key={item.id}
              className={`${styles.tabButton} ${activeTab === item.id ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(item.id)}
            >
              {item.serialNumber || `Item ${item.id.substr(-4)}`}
            </button>
          ))}
        </div>
        
        <div className={styles.tabContent}>
          {activeTab === 'details' ? (
            renderOverviewTab()
          ) : (
            renderIndividualItemTab(individualItems.find(item => item.id === activeTab))
          )}
        </div>
      </div>
    );
  };

  // Overview tab content
  const renderOverviewTab = () => {
    // Use groupData for overview
    if (!groupData) return null;
    
    return (
      <div className={styles.equipmentDetails}>
        <div className={styles.imageSection}>
          <div className={styles.imageContainer}>
            {groupData.coverPhotoUrl ? (
              <img 
                src={groupData.coverPhotoUrl} 
                alt={groupData.name} 
                className={styles.equipmentImage} 
              />
            ) : (
              <div className={styles.placeholderImage}>
                <span>{groupData.name.charAt(0)}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.infoSection}>
          <div className={styles.infoHeader}>
            <h1 className={styles.equipmentName}>{groupData.name}</h1>
            <div className={styles.categoryBadge}>
              {getCategoryDisplayName(groupData.inventoryType)}
            </div>
          </div>
          
          <div className={styles.basicInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Type:</span>
              <span className={styles.infoValue}>{groupData.type}</span>
            </div>
            {groupData.subType && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Sub Type:</span>
                <span className={styles.infoValue}>{groupData.subType}</span>
              </div>
            )}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Brand:</span>
              <span className={styles.infoValue}>{groupData.brand}</span>
            </div>
            {groupData.model && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Model:</span>
                <span className={styles.infoValue}>{groupData.model}</span>
              </div>
            )}
            
            <div className={styles.quantityInfo}>
              <span className={styles.quantityLabel}>Assigned Quantity</span>
              <span className={styles.quantityValue}>
                <span className={styles.totalQuantity}>{groupData.totalQuantity || individualItems.length}</span>
              </span>
            </div>
            {groupData.rentPerDay && (
              <div className={styles.priceInfo}>
                <span className={styles.priceLabel}>Rent Per Day</span>
                <span className={styles.priceValue}>₹{groupData.rentPerDay}</span>
              </div>
            )}
          </div>
          
          {groupData.description && (
            <div className={styles.descriptionSection}>
              <h3 className={styles.sectionTitle}>Description</h3>
              <p className={styles.descriptionText}>{groupData.description}</p>
            </div>
          )}
          
          {groupData.fileUrl && (
            <div className={styles.attachmentSection}>
              <h3 className={styles.sectionTitle}>Attachment</h3>
              <button 
                className={styles.attachmentButton}
                onClick={() => handleViewFile(groupData)}
              >
                <FontAwesomeIcon 
                  icon={groupData.fileUrl.toLowerCase().endsWith('.pdf') ? faFilePdf : faFileAlt} 
                  className={styles.attachmentIcon}
                />
                <span className={styles.attachmentName}>
                  {getFileName(groupData.fileUrl)}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Individual item tab content
  const renderIndividualItemTab = (item) => {
    if (!item) return <div className={styles.noItemData}>Item not found</div>;
    
    return (
      <div className={styles.individualItemDetails}>
        <div className={styles.itemHeader}>
          <div className={styles.serialNumberBadge}>
            SN: {item.serialNumber || 'N/A'}
          </div>
          <div className={styles.headerButtons}>
            <button
              className={styles.qrCodeButton}
              onClick={() => handleQrCodeClick(item)}
              title="View QR Code"
            >
              <FontAwesomeIcon icon={faQrcode} />
            </button>
          </div>
        </div>
        
        {/* Item details in a layout similar to overview tab */}
        <div className={styles.equipmentDetails}>
          <div className={styles.imageSection}>
            <div className={styles.imageContainer}>
              {item.photoUrl ? (
                <img 
                  src={item.photoUrl} 
                  alt={`${item.name} - ${item.serialNumber || 'N/A'}`} 
                  className={styles.equipmentImage} 
                />
              ) : groupData?.coverPhotoUrl ? (
                <img 
                  src={groupData.coverPhotoUrl} 
                  alt={item.name} 
                  className={styles.equipmentImage} 
                />
              ) : (
                <div className={styles.placeholderImage}>
                  <span>{item.name ? item.name.charAt(0) : 'I'}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className={styles.infoSection}>
            <div className={styles.infoHeader}>
              <h1 className={styles.equipmentName}>{item.name || groupData.name}</h1>
              {groupData.inventoryType && (
                <div className={styles.categoryBadge}>
                  {getCategoryDisplayName(groupData.inventoryType)}
                </div>
              )}
            </div>
            
            <div className={styles.basicInfo}>
              {item.model && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Model:</span>
                  <span className={styles.infoValue}>{item.model}</span>
                </div>
              )}
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Brand:</span>
                <span className={styles.infoValue}>{item.brand || groupData.brand}</span>
              </div>
              
              {item.rentPerDay && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Rent Per Day:</span>
                  <span className={styles.infoValue}>₹{item.rentPerDay}</span>
                </div>
              )}
              
              {/* Display check-in status if available */}
              {item.checkInStatus && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Check-In Status:</span>
                  <span className={`${styles.infoValue} ${item.checkInStatus.isCheckedIn ? styles.statusChecked : styles.statusUnchecked}`}>
                    {item.checkInStatus.isCheckedIn ? 'Checked In' : 'Not Checked In'}
                  </span>
                </div>
              )}
              
              {/* Display check-out status if available */}
              {item.checkOutStatus && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Check-Out Status:</span>
                  <span className={`${styles.infoValue} ${item.checkOutStatus.isCheckedOut ? styles.statusChecked : styles.statusUnchecked}`}>
                    {item.checkOutStatus.isCheckedOut ? 'Checked Out' : 'Not Checked Out'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.itemDetailsGrid}>
          {/* Assigned Staff Section with clickable items */}
          {item.assignedStaff && item.assignedStaff.length > 0 && (
            <div className={styles.assignedStaffSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faUser} className={styles.sectionIcon} />
                Assigned Staff
              </h3>
              
              <div className={styles.staffList}>
                {item.assignedStaff.map((staffId) => {
                  const staff = staffDetails[staffId] || { id: staffId };
                  return (
                    <div 
                      key={staffId} 
                      className={styles.staffItem}
                      onClick={() => handleStaffClick(staff)}
                    >
                      <div className={styles.staffItemContent}>
                        <div className={styles.staffImageContainer}>
                          {staff.profilePictureUrl ? (
                            <img 
                              src={staff.profilePictureUrl} 
                              alt={staff.fullName} 
                              className={styles.staffImage}
                            />
                          ) : (
                            <div className={styles.defaultStaffImage}>
                              <FontAwesomeIcon icon={faUser} />
                            </div>
                          )}
                        </div>
                        <div className={styles.staffInfo}>
                          <div className={styles.staffName}>
                            {staff.fullName || `Staff (${staffId.substring(0, 6)}...)`}
                          </div>
                          {staff.userName && (
                            <div className={styles.staffUsername}>
                              @{staff.userName} {staff.userSubType && `• ${staff.userSubType}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Assigned Dates Section */}
          {item.assignedDates && item.assignedDates.length > 0 && (
            <div className={styles.assignedDatesSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faCalendarAlt} className={styles.sectionIcon} />
                Assigned Dates
              </h3>
              
              <div className={styles.datesList}>
                {item.assignedDates.map((dateInfo, index) => (
                  <div key={index} className={styles.dateItem}>
                    <div className={styles.dateValue}>
                      {formatDate(dateInfo.date)}
                    </div>
                    
                    {dateInfo.event && 
                    dateInfo.event.venueDetails && 
                    dateInfo.event.venueDetails.venueData && (
                      <div className={styles.venueInfo}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.venueIcon} />
                        <span className={styles.venueName}>
                          {dateInfo.event.venueDetails.venueData.displayName || 'Unknown Venue'}
                        </span>
                        <div className={styles.venueAddress}>
                          {dateInfo.event.venueDetails.venueData.formattedAddress || 'No address available'}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  
  const handleStaffClick = (staff) => {
    if (staff.userName) {
      // Navigate to staff member's profile
      navigate(`/${staff.userName}`);
    }
  };

  // QR Code Modal
  const renderQrCodeModal = () => {
    if (!selectedQrItem) return null;

    // Generate QR code URL
    const qrCodeUrl = `${APP_URL}/scan/${selectedQrItem.id}`;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowQrModal(false)}>
        <div className={styles.qrCodeModal} onClick={(e) => e.stopPropagation()}>
          <h3 className={styles.qrCodeModalTitle}>
            QR Code for {selectedQrItem.serialNumber || 'Item'}
          </h3>
          
          <div className={styles.qrCodeModalContent} id="qr-code-print-area">
            <div className={styles.qrCodeContainer}>
              <div style={{ background: 'white', padding: '16px', margin: '0 auto' }}>
                <QRCode
                  size={256}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  value={qrCodeUrl}
                  viewBox={`0 0 256 256`}
                />
              </div>
              <h3>{selectedQrItem.name || groupData.name}</h3>
              <p><strong>Model: {selectedQrItem.model || 'N/A'}</strong></p>
              <p>{selectedQrItem.serialNumber || 'No Serial Number'}</p>
              <p>{selectedQrItem.brand || groupData.brand}</p>
            </div>
          </div>
          
          <div className={styles.qrCodeModalActions}>
            <button 
              className={styles.printButton} 
              onClick={handlePrintQrCode}
            >
              <FontAwesomeIcon icon={faPrint} className={styles.buttonIcon} />
              Print QR Code
            </button>
            <button 
              className={styles.closeButton} 
              onClick={() => setShowQrModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Content to display based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
          <p>Loading equipment details...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className={styles.errorContainer}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.errorIcon} />
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Inventory</span>
          </button>
        </div>
      );
    }
    
    if (!groupData) {
      return (
        <div className={styles.notFoundContainer}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.notFoundIcon} />
          <h2>Item Not Found</h2>
          <p>The requested equipment item could not be found.</p>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Inventory</span>
          </button>
        </div>
      );
    }

    return (
      <div className={styles.detailsContainer}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Assigned Inventory</span>
          </button>
        </div>
        
        {/* Render overview and individual item tabs */}
        {renderTabs()}
      </div>
    );
  };

  return (
    <div className={styles.inventoryContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['equipment-details-main']}>
          {renderContent()}
          
          {/* QR Code Modal */}
          {showQrModal && renderQrCodeModal()}
        </main>
        {!isMobile && <RightMenu tours={tours} />}
      </div>
      
      {/* File Viewer Modal */}
      {groupData && (
        <FileViewerModal
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          file={{
            fileUrl: fileViewerUrl,
            fileType: fileViewerType,
            fileName: fileViewerName || 'Document'
          }}
        />
      )}
    </div>
  );
};

export default AssignedEquipmentCardDetails;